<template>
  <div
    :class="{ 'load load--fill' : isLoading }"
    class="p-4 py-8"
  >
    <section class="max-w-lg mx-auto">
      <form @submit.prevent>
        <fieldset>
          <div class="field-list">
            <div class="field mb-4">
              <input
                id="pageTitle"
                v-model="form.title"
                name="pageTitle"
                :class="{ error: hasError('title') }"
                placeholder="Page Title"
                type="text"
              >
              <div class="field__meta">
                <span
                  v-if="hasError('title')"
                  class="field__meta__help"
                  v-text="errors.title[0]"
                />
              </div>
            </div>
            <div class="field mb-8">
              <div v-if="hasLeadImage">
                <img
                  :src="form.hero_image"
                  class="rounded"
                >
              </div>
              <div
                v-else
                class="w-full flex justify-center"
              >
                <image-cropper
                  ref="image-cropper"
                  :aspect-ratio="3/2"
                  :crop-button-settings="{
                    innerText: isUploading ? 'Uploading...' : 'Crop & Upload Image',
                    disabled: isUploading
                  }"
                  :extra-class="'union-logo'"
                  @crop="onImageCropped"
                  @reset="onCropperReset"
                />
              </div>
              <div class="flex flex-row-reverse mt-2 items-baseline">
                <a
                  v-if="hasLeadImage"
                  class="text-xs"
                  href="#"
                  @click.prevent="removeLeadImage"
                  v-text="'Remove this image?'"
                />
              </div>
            </div>
            <div class="field">
              <Redactor
                v-model="form.content"
                :config="redactorConfig"
              />
            </div>
          </div>
          <div class="py-8 flex field">
            <div class="w-1/4 pr-4">
              <label
                for="resource_category"
                v-text="'Category'"
              />
              <select
                id="resource_category"
                v-model="form.category"
                name="resource_category"
                :class="{ error : hasError('category') }"
              >
                <template v-if="categories.length">
                  <option
                    v-for="category in categories"
                    :key="category.id"
                    :value="category.attributes.slug"
                    v-text="category.attributes.title"
                  />
                </template>
              </select>
              <span
                v-if="hasError('category')"
                class="help-block"
                v-text="'This field is required!'"
              />
            </div>
            <div class="w-1/4 pl-4">
              <label
                for="resource_icon"
                v-text="'Icon'"
              />
              <ResourceIconSelector
                id="resource_icon"
                v-model="form.icon"
                :class="{ error : hasError('icon') }"
              />
            </div>
            <div class="w-1/2 pl-4">
              <label
                for="visibility"
                v-text="'Restrict visibility to:'"
              />
              <Multiselect
                id="visibility"
                v-model="selectedMembershipGroups"
                :loading="isLoadingGroups"
                :multiple="true"
                :options="availableGroups"
                label="label"
                open-direction="below"
                placeholder="Group(s)"
                track-by="uuid"
                :clear-on-select="false"
                :close-on-select="false"
                @search-change="handleSearchChange"
              />
              <template
                slot="tag"
                slot-scope="props"
              >
                <div
                  class="category-modal__group hover:bg-grey py-1 px-2 my-1 mr-2 rounded-full cursor-pointer flex"
                  @click.stop="props.remove(props.option)"
                >
                  <div
                    class="flex-1"
                    v-text="props.option.attributes.title"
                  />
                  <div
                    class="category-modal__group__remove"
                    v-text="'x'"
                  />
                </div>
              </template>
              <template
                slot="option"
                slot-scope="props"
              >
                <div class="option__desc">
                  <span
                    class="option__title text-xs"
                    v-text="props.option.attributes.title"
                  />
                </div>
              </template>
            </div>
          </div>
        </fieldset>
        <div class="text-center mt-6">
          <button
            type="button"
            :class="{ load : isLoading }"
            :disabled="isUploading"
            :aria-disabled="isUploading"
            class="btn"
            @click="save"
            v-text="actionButtonText"
          />
        </div>
      </form>
    </section>
  </div>
</template>

<script type="text/babel">
import { debounce } from 'lodash'
import { mapState, mapActions } from 'vuex'
import Multiselect from 'vue-multiselect'
import ResourceIconSelector from 'Resource/Resources/assets/js/components/ResourceIconSelector'
import ImageCropper from 'Base/Resources/assets/js/components/ImageCropper'

export default {
  name: 'PageResourceForm',
  components: {
    ImageCropper,
    Multiselect,
    ResourceIconSelector,
  },
  props: {
    pageUuid: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      isLoading: false,
      isLoadingGroups: false,
      isUploading: false,
      errors: {},
      form: {
        title: '',
        content: '',
        hero_image: '',
        uuid: '',
        icon: '',
        category: '',
        visibility_permissions: [],
      },
      selectedMembershipGroups: [],
      redactorConfig: {
        plugins: ['video','alignment'],
      },
    }
  },
  computed: {
    ...mapState({
      page: state => state.resource.page,
      membershipGroups: state => state.membership.membershipGroups.data,
      categories: state => state.resource.categories,
    }),
    exists() {
      return !!this.form.uuid
    },
    hasLeadImage() {
      return !!this.form.hero_image
    },
    actionButtonText() {
      return this.exists ? 'Update Page' : 'Create Page'
    },
    availableGroups() {
      return _.map(this.membershipGroups, group => ({
        uuid: group.uuid,
        label: group.attributes.title,
      }))
    },
  },
  watch: {
    selectedMembershipGroups() {
      this.form.visibility_permissions = this.selectedMembershipGroups.map(group => group.uuid)
    },
  },
  beforeMount() {
    this.$store.dispatch('resourceGetCategories').then(() => {
      this.form.category = this.categories[0].attributes.slug
    })
  },
  async mounted() {
    await this.$nextTick()

    this.isLoading = true
    this.form.uuid = this.pageUuid

    this.fetchMembershipGroups().then(() => {
      if (this.pageUuid) {
        this.$store.dispatch('resourceGetPage', this.pageUuid).then((response) => {
          this.fillForm(response.data)
          this.isLoading = false
        })
      } else {
        this.isLoading = false
      }
    })
  },
  methods: {
    ...mapActions({
      fetchMembershipGroups: 'membershipGetMembershipGroups',
    }),
    fillForm(page) {
      this.form.title = page.attributes.title
      this.form.content = page.attributes.content
      this.form.hero_image = page.attributes.hero_image
      this.form.icon = page.attributes.icon
      this.form.category = page.category.attributes.slug
      this.form.visibility_permissions = page.attributes.visibility_permissions

      this.setSelectedMembershipGroups(page)
    },
    save() {
      this.isLoading = true

      const action = this.exists ? 'put' : 'post'
      const url = this.exists ? `pages/${this.form.uuid}` : 'pages'

      this.$api
        .active()
        [action](url, this.form)
        .then((response) => {
          window.location = this.$urlGenerator.active(
            'resources/pages/' + response.data.uuid
          )
        })
        .catch((errors) => {
          this.errors = errors.response.data.errors
          this.isLoading = false
        })
    },
    hasError(field) {
      return this.errors.hasOwnProperty(field)
    },
    uploadError(error) {
      this.isUploading = false
      this.errors.uploadError = error
    },
    removeLeadImage() {
      this.form.hero_image = ''
    },
    fileIsUploading() {
      this.isUploading = true
    },
    handleSearchChange: debounce(function (search) {
      this.isLoadingGroups = true

      this.fetchMembershipGroups({
        params : {
          query: search,
        },
      }).then(() => {
        this.isLoadingGroups = false
      })
    }, 500),
    uploadHasErrored(file, response) {
      let errorMessage = response.message

      this.isUploading = false

      if (response.errors.hero_image) {
        errorMessage = response.errors.hero_image[0]
      }

      file.previewElement.querySelectorAll('.dz-error-message')[0].innerHTML = errorMessage

      this.$store.commit('baseAddNotification', {
        type: 'error',
        title: 'Error',
        text: errorMessage,
      })

      this.errors = response.errors
    },
    fileHasBeenUploaded(file, response) {
      this.form.hero_image = response
      this.isUploading = false
    },
    setSelectedMembershipGroups(page) {
      this.selectedMembershipGroups = page.visibilityGroups.data.map(group => ({
        label: group.attributes.title,
        uuid: group.uuid,
      }))
    },
    onImageCropped(image) {
      this.fileIsUploading()

      let formData = new FormData()
      formData.append('image', image, image.type.replace('\/', '.'))

      let headers = {
        'Content-Type': 'multipart/form-data',
      }

      this.$api.active().post('images?storagePath=pages', formData, headers)
        .then((response) => {
          this.fileHasBeenUploaded(image, response.data)
        })
        .catch((error) => {
          this.uploadHasErrored(image, error.response.data)
          this.uploadError(error.response.data.errors.image[0])
          this.removeLeadImage()
        })
    },
    onCropperReset() {
      this.form.hero_image = ''
    },
  },
}
</script>
<style>
 .image-cropper {
   width: 100%;
   max-width: 784px;
 }
</style>